import {
  ArcElement,
  Chart as ChartJS,
  Legend,
  Tooltip
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import LinkClicksChartHeader from './LinkClicksChartHeader';
import { DEVICE_LABEL_KEYS, DEVICE_COLORS } from './constants';
import { useTranslation } from 'react-i18next';

ChartJS.register(ArcElement, Tooltip, Legend);

const createCenterLabelPlugin = (share, label) => ({
  id: 'linkClicksCenterLabel',
  afterDraw(chart) {
    const { ctx, chartArea } = chart;
    if (!chartArea) return;

    const centerX = (chartArea.left + chartArea.right) / 2;
    const centerY = (chartArea.top + chartArea.bottom) / 2;

    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#3e4957';
    ctx.font = '700 28px Arial';
    ctx.fillText(`${share}%`, centerX, centerY - 5);
    ctx.fillStyle = '#8b929a';
    ctx.font = '400 11px Arial';
    ctx.fillText(label, centerX, centerY + 18);
    ctx.restore();
  }
});

const ClicksByDevice = ({ devices }) => {
  const { t } = useTranslation();
  const getDeviceLabel = (device) => t(DEVICE_LABEL_KEYS[device]);
  const [topDevice] = devices;

  if (!topDevice) {
    return (
      <section className="bg-white rounded-4 p-3 p-md-4 h-100">
        <LinkClicksChartHeader
          title={t('linkClicks.clicksByDevice')}
          description={t('linkClicks.clicksByDeviceDescription')}
        />
        <div className="link-clicks-device-chart" aria-hidden="true" />
      </section>
    );
  }

  const data = {
    labels: devices.map(({ device }) => getDeviceLabel(device)),
    datasets: [
      {
        data: devices.map(({ share }) => share),
        backgroundColor: devices.map(({ device }) => DEVICE_COLORS[device]),
        borderWidth: 0,
        hoverOffset: 3
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '62%',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: { label: ({ label, raw }) => `${label}: ${raw}%` }
      }
    }
  };

  return (
    <section className="bg-white rounded-4 p-3 p-md-4 h-100">
      <LinkClicksChartHeader
        title={t('linkClicks.clicksByDevice')}
        description={t('linkClicks.clicksByDeviceDescription')}
      />
      <div className="d-flex flex-column flex-sm-row align-items-center justify-content-center gap-3">
        <div className="link-clicks-device-chart flex-shrink-0">
          <Doughnut
            data={data}
            options={options}
            plugins={[
              createCenterLabelPlugin(
                topDevice.share,
                getDeviceLabel(topDevice.device)
              )
            ]}
            aria-label={t('linkClicks.clicksByDevice')}
          />
        </div>
        <ul className="d-flex flex-column gap-2 mb-0">
          {devices.map(({ device, share }) => (
            <li key={device} className="d-flex align-items-center gap-2 fs-7 text-nowrap">
              <span
                className="link-clicks-legend-dot rounded-circle flex-shrink-0"
                style={{ backgroundColor: DEVICE_COLORS[device] }}
                aria-hidden="true"
              />
              <span>{getDeviceLabel(device)}</span>
              <span className="fw-bold">{share}%</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default ClicksByDevice;
